/**
 * @fileoverview Pure, defensive helpers for the StudentTasks module. Date inputs
 * may arrive as ISO (`YYYY-MM-DD`) or already localized (`DD/MM/YYYY`) — both are
 * handled without throwing, and empty values fall back to an em dash.
 */

export const EMPTY_VALUE = '—'

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

/** Formats a date to `DD/MM/YYYY`; passes through already-localized values. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY_VALUE
  const iso = ISO_DATE.exec(value)
  if (iso) {
    const [, year, month, day] = iso
    return `${day}/${month}/${year}`
  }
  return value
}

/** Builds the "DD/MM/YYYY - Title" lesson label, defensively. */
export function lessonLabel(
  date: string | null | undefined,
  title: string | null | undefined,
): string {
  const safeTitle = title ?? ''
  if (!date) return safeTitle || EMPTY_VALUE
  const formatted = formatDate(date)
  return safeTitle ? `${formatted} - ${safeTitle}` : formatted
}

/** Seeds the score-by-id map from the items' already-saved student scores. */
export function initialScores(
  items: readonly { readonly id: string; readonly studentScore: number }[],
): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    acc[item.id] = item.studentScore ?? 0
    return acc
  }, {})
}

/** True when an ISO/localized date is strictly before today (local midnight). */
export function isOverdue(value: string | null | undefined): boolean {
  if (!value) return false
  const iso = ISO_DATE.exec(value)
  if (!iso) return false
  const [, year, month, day] = iso.map(Number) as unknown as number[]
  const due = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due.getTime() < today.getTime()
}
